// components/Modals/CirurgiaDetailsModal/index.tsx
import React, { useState, useEffect } from "react";
import { formatDate } from "../../../utils/date.util.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import { useCirurgiaOpcoes, Entidade } from "../../../hooks/useCirurgiaOpcoes.ts";
import { usePlanLimits } from "../../../hooks/usePlanLimits.ts";
import Drawer from "../../../ui/Drawer.tsx";
import Button from "../../../ui/Button.tsx";
import StatusBadge from "../../../ui/StatusBadge.tsx";
import FormField from "../../../ui/FormField.tsx";
import "../../../styles/design-system.css";

const formatarDataParaInput = (dataString: string | undefined | null): string => {
  if (!dataString) return "";
  try {
    const data = new Date(dataString);
    if (isNaN(data.getTime())) return "";
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    const hora = String(data.getHours()).padStart(2, "0");
    const minuto = String(data.getMinutes()).padStart(2, "0");
    return `${ano}-${mes}-${dia}T${hora}:${minuto}:00`;
  } catch {
    return "";
  }
};

interface Props {
  open: boolean;
  onClose: () => void;
  cirurgia: any | null;
  modoEdicao?: boolean;
  onSalvar?: (dados: any) => Promise<void>;
  /**
   * Botões extras no rodapé do modo leitura, antes do "Fechar". A tela de
   * Agenda usa para oferecer Editar / mudar status / Cancelar sem sair do
   * detalhe. Quem não passa nada continua vendo só o "Fechar", como antes.
   *
   * Fica a cargo de quem chama decidir quais botões mostrar, porque é lá que
   * estão as permissões — este componente não consulta `usePermissoes`.
   */
  acoesLeitura?: React.ReactNode;
}

const CirurgiaDetailsModal: React.FC<Props> = ({ open, onClose, cirurgia, modoEdicao = false, onSalvar, acoesLeitura }) => {
  const { showFeedback } = useFeedback();
  const [isLoading, setIsLoading] = useState(false);
  const [dados, setDados] = useState<any>({});
  const { opcoes: listas, carregando } = useCirurgiaOpcoes();
  const { limits: planLimits } = usePlanLimits();

  // Quantos campos de instrumentador mostrar. Nunca menos do que a cirurgia já
  // tem preenchido: se a clínica baixou o limite pra 1 mas este registro foi
  // criado com 3, esconder os outros dois apagaria dado ao salvar. Espelha a
  // mesma regra do backend em CirurgiaService.validarQuantidadeInstrumentadores.
  const instrumentadoresPreenchidos = [
    cirurgia?.instrumentador1Id || cirurgia?.instrumentador1,
    cirurgia?.instrumentador2Id || cirurgia?.instrumentador2,
    cirurgia?.instrumentador3Id || cirurgia?.instrumentador3,
  ].filter(Boolean).length;

  const camposInstrumentador = Math.min(
    3,
    Math.max(planLimits?.maxInstrumentadores || 3, instrumentadoresPreenchidos)
  );

  useEffect(() => {
    if (cirurgia && modoEdicao && open) {
      const idCirurgia = cirurgia.id || cirurgia.idCirurgia || cirurgia.cirurgiaId;
      setDados({
        id: idCirurgia,
        data: formatarDataParaInput(cirurgia.data),
        tipoCirurgia: cirurgia.tipoCirurgia?.id || cirurgia.tipoCirurgiaId || null,
        observacaoTipoCirurgia: cirurgia.observacaoTipoCirurgia || cirurgia.observacao_tipo_cirurgia || null,
        medico: cirurgia.medico?.id || cirurgia.medicoId || null,
        paciente: cirurgia.paciente?.id || cirurgia.pacienteId || null,
        instrumentador1: cirurgia.instrumentador1Id || cirurgia.instrumentador1?.id || null,
        instrumentador2: cirurgia.instrumentador2Id || cirurgia.instrumentador2?.id || null,
        instrumentador3: cirurgia.instrumentador3Id || cirurgia.instrumentador3?.id || null,
        hospital: cirurgia.hospital?.id || cirurgia.hospitalId || null,
        convenio: cirurgia.convenioId || cirurgia.convenio?.id || null,
        observacao: cirurgia.observacao || null,
        status: cirurgia.status,
      });
    }
  }, [cirurgia, modoEdicao, open]);

  // Um paciente/convênio/médico/etc. vinculado a esta cirurgia pode ter sido
  // desativado depois de ela ter sido criada — nesse caso o id salvo não bate
  // com nenhuma opção da lista (que só traz ativos), e o <select> controlado
  // renderiza sem seleção visível mesmo com o dado certo em `dados`. Injeta uma
  // opção sintética usando o nome que já vem no próprio DTO da cirurgia.
  const comOpcaoInativa = (lista: Entidade[], idSelecionado: string | null | undefined, nomeSalvo: string | null | undefined) => {
    if (!idSelecionado || lista.some((o) => o.id === idSelecionado)) return lista;
    return nomeSalvo ? [...lista, { id: idSelecionado, nome: `${nomeSalvo} (inativo)` }] : lista;
  };

  const handleChange = (campo: string, valor: any) => {
    const valorFormatado = valor === "" ? null : valor;
    setDados((prev: any) => ({ ...prev, [campo]: valorFormatado }));
  };

  const handleSalvar = async () => {
    if (!dados?.id) {
      showFeedback("ERRO CRÍTICO: ID da cirurgia não encontrado", "error");
      return;
    }
    const faltando = [
      !dados.medico && "Médico",
      !dados.paciente && "Paciente",
      !dados.data && "Data",
    ].filter(Boolean) as string[];

    if (faltando.length > 0) {
      showFeedback(
        faltando.length === 1
          ? `Preencha o campo ${faltando[0]}.`
          : `Preencha os campos ${faltando.slice(0, -1).join(", ")} e ${faltando[faltando.length - 1]}.`,
        "error"
      );
      return;
    }

    const [datePart, timePart] = dados.data.split("T");
    const [ano, mes, dia] = datePart.split("-").map(Number);
    const [hora, minuto] = timePart.split(":").map(Number);
    const dataSelecionada = new Date(ano, mes - 1, dia, hora, minuto);
    const agora = new Date();
    agora.setSeconds(0, 0);

    if (dataSelecionada <= agora) {
      showFeedback("A data e hora da cirurgia deve ser posterior ao momento atual", "error");
      return;
    }

    if (!onSalvar) return;
    setIsLoading(true);
    try {
      await onSalvar(dados);
      showFeedback("Cirurgia alterada com sucesso!", "success");
      onClose();
    } catch (error: any) {
      showFeedback(error?.message || "Erro ao salvar alterações", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!cirurgia) return null;

  // Modo visualização (apenas leitura)
  if (!modoEdicao) {
    const blocos: { titulo: string; linhas: [string, React.ReactNode][] }[] = [
      { titulo: "Cirurgia", linhas: [
        ["Data e hora", formatDate(cirurgia.data)],
        ["Tipo de cirurgia", cirurgia.tipoCirurgia],
        ["Status", <StatusBadge status={cirurgia.status} />],
      ]},
      { titulo: "Paciente", linhas: [["Nome", cirurgia.paciente]] },
      { titulo: "Equipe", linhas: [
        ["Médico", cirurgia.medico],
        ["Instrumentador 1", cirurgia.instrumentador1 || "—"],
        ...(camposInstrumentador >= 2
          ? [["Instrumentador 2", cirurgia.instrumentador2 || "—"] as [string, any]]
          : []),
        ...(camposInstrumentador >= 3
          ? [["Instrumentador 3", cirurgia.instrumentador3 || "—"] as [string, any]]
          : []),
      ]},
      { titulo: "Local e convênio", linhas: [
        ["Hospital", cirurgia.hospital],
        ["Convênio", cirurgia.convenio || "—"],
      ]},
      { titulo: "Observações", linhas: [
        ["Observação do tipo de cirurgia", cirurgia.observacaoTipoCirurgia || "—"],
        ["Observação", cirurgia.observacao || "—"],
        ...(cirurgia.status === "CANCELADA" && cirurgia.motivo
          ? [["Motivo do cancelamento", cirurgia.motivo] as [string, React.ReactNode]]
          : []),
      ]},
    ];

    return (
      <Drawer
        open={open}
        onClose={onClose}
        title="Detalhes da cirurgia"
        footer={
          acoesLeitura ? (
            <>
              {acoesLeitura}
              {/* Outline e não primary: com ações no rodapé, o azul sólido é do
                  "Editar". Dois botões cheios disputando o olho não ajudam, e
                  fechar já tem o X no cabeçalho. */}
              <Button variant="outline" onClick={onClose}>Fechar</Button>
            </>
          ) : (
            // Sem ações extras o Fechar ocupa a largura toda, como sempre foi.
            <Button variant="primary" onClick={onClose} style={{ width: "100%" }}>Fechar</Button>
          )
        }
      >
        {blocos.map((bloco) => (
          <div className="ds-info-block" key={bloco.titulo}>
            <h4>{bloco.titulo}</h4>
            {bloco.linhas.map(([label, value]) => (
              <div className="ds-info-row" key={label}>
                <span className="k">{label}</span>
                <span className="v">{value}</span>
              </div>
            ))}
          </div>
        ))}
      </Drawer>
    );
  }

  // Modo edição
  if (carregando) {
    return (
      <Drawer open={open} onClose={onClose} title="Editar cirurgia">
        <p style={{ color: "var(--ink-faint)", padding: "20px 0" }}>Carregando dados...</p>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Editar cirurgia"
      footer={
        <Button
          variant="primary"
          onClick={handleSalvar}
          disabled={isLoading || !dados.medico || !dados.paciente || !dados.data}
          style={{ width: "100%" }}
        >
          {isLoading ? "Salvando..." : "Salvar alterações"}
        </Button>
      }
    >
      <div className="ds-form-section">
        <div className="ds-form-grid">
          <FormField label="Data e hora" span2>
            <input type="datetime-local" value={dados.data || ""} onChange={(e) => handleChange("data", e.target.value)} />
          </FormField>

          <FormField label="Médico">
            <select value={dados.medico || ""} onChange={(e) => handleChange("medico", e.target.value)}>
              <option value="">Selecione</option>
              {comOpcaoInativa(listas.medicos, dados.medico, cirurgia.medico).map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
          </FormField>
          <FormField label="Paciente">
            <select value={dados.paciente || ""} onChange={(e) => handleChange("paciente", e.target.value)}>
              <option value="">Selecione</option>
              {comOpcaoInativa(listas.pacientes, dados.paciente, cirurgia.paciente).map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </FormField>

          <FormField label="Instrumentador 1">
            <select value={dados.instrumentador1 || ""} onChange={(e) => handleChange("instrumentador1", e.target.value)}>
              <option value="">Selecione</option>
              {comOpcaoInativa(listas.instrumentadores, dados.instrumentador1, cirurgia.instrumentador1).map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
            </select>
          </FormField>
          {camposInstrumentador >= 2 && (
            <FormField label="Instrumentador 2" optional>
              <select value={dados.instrumentador2 || ""} onChange={(e) => handleChange("instrumentador2", e.target.value)}>
                <option value="">Nenhum</option>
                {comOpcaoInativa(listas.instrumentadores, dados.instrumentador2, cirurgia.instrumentador2).map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
              </select>
            </FormField>
          )}
          {camposInstrumentador >= 3 && (
            <FormField label="Instrumentador 3" optional>
              <select value={dados.instrumentador3 || ""} onChange={(e) => handleChange("instrumentador3", e.target.value)}>
                <option value="">Nenhum</option>
                {comOpcaoInativa(listas.instrumentadores, dados.instrumentador3, cirurgia.instrumentador3).map((i) => <option key={i.id} value={i.id}>{i.nome}</option>)}
              </select>
            </FormField>
          )}

          <FormField label="Hospital">
            <select value={dados.hospital || ""} onChange={(e) => handleChange("hospital", e.target.value)}>
              <option value="">Selecione</option>
              {comOpcaoInativa(listas.hospitais, dados.hospital, cirurgia.hospital).map((h) => <option key={h.id} value={h.id}>{h.nome}</option>)}
            </select>
          </FormField>
          <FormField label="Convênio" optional>
            <select value={dados.convenio || ""} onChange={(e) => handleChange("convenio", e.target.value)}>
              <option value="">Nenhum</option>
              {comOpcaoInativa(listas.convenios, dados.convenio, cirurgia.convenio).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </FormField>

          <FormField label="Tipo de cirurgia" span2>
            <select value={dados.tipoCirurgia || ""} onChange={(e) => handleChange("tipoCirurgia", e.target.value)}>
              <option value="">Selecione</option>
              {comOpcaoInativa(listas.tipoCirurgias, dados.tipoCirurgia, cirurgia.tipoCirurgia).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </FormField>

          <FormField label="Observação do tipo de cirurgia" span2>
            <textarea rows={2} value={dados.observacaoTipoCirurgia || ""} onChange={(e) => handleChange("observacaoTipoCirurgia", e.target.value)} />
          </FormField>
          <FormField label="Observação" span2>
            <textarea rows={2} value={dados.observacao || ""} onChange={(e) => handleChange("observacao", e.target.value)} />
          </FormField>

          <FormField label="Status" span2>
            <input value={dados.status || ""} disabled />
          </FormField>
        </div>
      </div>
    </Drawer>
  );
};

export default CirurgiaDetailsModal;
